<script lang="ts">
/**
 * BaseCard usage patterns
 * - Entity tiles (characters, personas): use variant="complex"; size="large" for primary character grids, size="medium" for persona/bot previews; keep imagePosition="top"; set selectable when the whole card navigates.
 * - Management lists (bots/personas settings): variant="complex" + size="medium" with actions slot; default background for lists, bg-surface-variant when showing a preview side-by-side.
 * - Nested cards (conversation groups): wrap groups in variant="complex" size="small"; inner list items also complex/small; reserve imagePosition="left|right" for compact rows.
 * - Accessibility: selectable adds button role, keyboard activation, and focus ring. Only enable when @click is provided, set a clear title (or an explicit aria-label) for screen readers, and pass imageAlt when using imageSrc.
  *
  * See `app/layout-primitives-usage.md` for canonical layout patterns and page-type recommendations.
 */
// Predefined variants for strict usage patterns
const VARIANTS = {
  COMPACT: 'compact',
  SIMPLE: 'simple',
  COMPLEX: 'complex',
} as const

const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
} as const

type CardVariant = typeof VARIANTS[keyof typeof VARIANTS]
type CardSize = typeof SIZES[keyof typeof SIZES]
type CardImagePosition = 'top' | 'left' | 'right'

interface BaseCardThemeEntry {
  container: string
  image: string
  title: string
  description: string
  focus: string
  subtitle?: string
}

const theme: Record<CardVariant, BaseCardThemeEntry> = {
  [VARIANTS.COMPACT]: {
    container: 'rounded-lg border border-border bg-surface-raised p-3 flex items-center gap-3 shadow-floating-low',
    image: 'rounded-md',
    title: 'text-sm font-medium text-on-surface truncate',
    description: 'text-xs text-text-secondary truncate',
    focus: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--theme-focus-ring-offset)] overflow-hidden',
  },
  [VARIANTS.SIMPLE]: {
    container: 'rounded-lg border border-border bg-surface-raised p-4 flex flex-col items-center text-center shadow-floating-low',
    image: 'rounded-md ',
    title: 'text-lg font-bold text-on-surface mb-2',
    description: 'text-sm text-text-secondary leading-relaxed line-clamp-3',
    focus: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--theme-focus-ring-offset)]',
    subtitle: 'text-md text-text-secondary mb-1 text-center overflow-hidden',
  },
  [VARIANTS.COMPLEX]: {
    container: 'rounded-lg border border-border bg-surface-raised p-6 shadow-floating-low',
    image: 'rounded-lg',
    title: 'text-h2 font-bold text-on-surface my-2 text-center',
    description: 'text-sm text-text-secondary leading-relaxed mb-4',
    focus: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--theme-focus-ring-offset)]',
    subtitle: 'text-md text-text-secondary mb-1 text-center overflow-hidden',
  },
}

const sizeClasses: Record<CardSize, { image: string; spacing: string }> = {
  [SIZES.SMALL]: {
    image: 'w-20 h-20',
    spacing: 'gap-2',
  },
  [SIZES.MEDIUM]: {
    image: 'w-24 h-24',
    spacing: 'gap-3',
  },
  [SIZES.LARGE]: {
    image: 'w-30 h-30',
    spacing: 'gap-4',
  },
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { parseDescriptionMarkdown } from '@/utils/markdown'
import BaseImage from '@/components/ui/BaseImage.vue'
import type { ImageFocus } from '@/lib/media/imageAssetFields'

interface BaseCardProps {
  title?: string
  description?: string
  markdownDescription?: string
  imageSrc?: string
  imageAlt?: string
  imageFocus?: ImageFocus | null
  selected?: boolean
  loading?: boolean
  disabled?: boolean
  selectable?: boolean
  square?: boolean
  minWidth?: string
  background?: string
  subtitle?: string
  descriptionClassOverride?: string
  variant?: CardVariant
  size?: CardSize
  imagePosition?: CardImagePosition
}

const props = withDefaults(defineProps<BaseCardProps>(), {
  title: '',
  description: '',
  markdownDescription: '',
  imageSrc: '',
  imageAlt: '',
  imageFocus: null,
  selected: false,
  loading: false,
  disabled: false,
  selectable: false,
  square: false,
  minWidth: '10',
  background: 'bg-surface-raised',
  subtitle: '',
  descriptionClassOverride: '',
  variant: VARIANTS.SIMPLE,
  size: SIZES.MEDIUM,
  imagePosition: 'top',
})

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
  (e: 'image-error', event: Event): void
}>()

// Computed classes based strictly on variant and size
const containerClasses = computed(() => {
  const baseClasses = [
    theme[props.variant].container,
    props.selectable ? theme[props.variant].focus : '',
    props.selectable ? 'cursor-pointer hover:shadow-interactive' : 'cursor-auto',
    sizeClasses[props.size].spacing,
    props.selected ? 'border-primary-border bg-surface-selected ring-2 ring-[var(--theme-focus-ring)] ring-offset-2 ring-offset-[var(--theme-focus-ring-offset)]' : '',
    props.loading ? 'opacity-50 cursor-not-allowed' : '',
    props.disabled ? 'opacity-30 pointer-events-none' : '',
    props.background,
    props.variant === VARIANTS.COMPACT && props.imagePosition !== 'top' ? 'flex-row' : 'flex-col',
    'base-card'
  ]

  return baseClasses.join(' ')
})

const imageClasses = computed(() => {
  return [
    'object-cover aspect-square',
    theme[props.variant].image,
    sizeClasses[props.size].image,
  ].join(' ')
})

const descriptionClasses = computed(() => theme[props.variant].description)

const handleClick = (event: MouseEvent): void => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}

const handleKeydown = (event: KeyboardEvent): void => {
  if ((event.key === 'Enter' || event.key === ' ') && !props.disabled && !props.loading) {
    event.preventDefault()
    emit('click', event as unknown as MouseEvent)
  }
}
</script>

<template>
  <div
    :class="containerClasses"
    :role="selectable ? 'button' : ''"
    tabindex="0"
    @click="handleClick"
    @keydown="handleKeydown"
    :aria-disabled="disabled || loading"
    :aria-label="title || 'Card'"
  >
    <!-- Image section with strict positioning -->
    <div
      v-if="imageSrc"
      class="flex justify-center"
    >
      <BaseImage
        :src="imageSrc"
        :alt="imageAlt"
        :image-focus="imageFocus"
        :class="imageClasses"
        fallback-size="lg"
      />
    </div>

    <!-- Content area with variant-specific structure -->
    <div class="w-full">
      <!-- Title - always shown if provided -->
      <h3
        v-if="title"
        :class="theme[props.variant].title"
      >
        {{ title }}
      </h3>

      <!-- Subtitle - shown based on variant -->
      <h4
        v-if="subtitle"
        :class="theme[props.variant].subtitle"
      >
        {{ subtitle }}
      </h4>

      <!-- Description - shown based on variant -->
      <div
        v-if="description && variant !== VARIANTS.COMPACT"
        :class="descriptionClasses"
        v-html="parseDescriptionMarkdown(description, variant === VARIANTS.COMPLEX ? 'full' : 'compact')"
      ></div>

      <!-- Markdown description - shown if provided -->
      <div
        v-if="markdownDescription"
        :class="['markdown']"
        v-html="parseDescriptionMarkdown(markdownDescription)"
      ></div>

      <!-- Additional content slot (for complex variant) -->
      <div
        v-if="$slots.content && variant === VARIANTS.COMPLEX"
        class="mt-3"
      >
        <slot name="content"></slot>
      </div>

      <!-- Full-width actions for complex variant -->
      <div
        v-if="$slots.actions"
        :class="'flex gap-2 mt-4 justify-evenly'"
      >
        <slot name="actions"></slot>
      </div>
    </div>

    <!-- Footer slot (complex variant only) -->
    <div
      v-if="$slots.footer"
      class="pt-2 mt-2 border-t border-border text-sm text-text-secondary"
    >
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<style scoped>
.base-card {
  min-width: 10rem !important;
}
</style>
